import type { AdminSubmission, SubmissionFile } from '@l2u/ui/lib/types.ts';

// View-data for one document_upload row, derived from its submission.
//  - 'file'     → at least one uploaded file; `primaryFile` drives Expiry/preview.
//  - 'adopted'  → satisfied by adopting a library document (no uploaded file).
//  - 'awaiting' → nothing submitted yet.
export type DocumentRowData =
  | { kind: 'file'; primaryFile: SubmissionFile; uploadedAt: string; expiresAt: string | null }
  | { kind: 'adopted'; documentId: string | null; uploadedAt: string | null }
  | { kind: 'awaiting' };

// The most urgent file: soonest expiry wins; if none expire, the most recently
// uploaded. (ISO-8601 strings compare correctly with `<`.)
function pickPrimaryFile(files: SubmissionFile[]): SubmissionFile {
  const withExpiry = files.filter((f) => f.expires_at);
  if (withExpiry.length > 0) {
    return withExpiry.reduce((soonest, f) => (f.expires_at! < soonest.expires_at! ? f : soonest));
  }
  return files.reduce((latest, f) => (f.created_at > latest.created_at ? f : latest));
}

function latestCreatedAt(files: SubmissionFile[]): string {
  return files.reduce((latest, f) => (f.created_at > latest ? f.created_at : latest), files[0]!.created_at);
}

// Whether the Compliance Documents tab shows the Compliant / Not Compliant /
// N/A buttons for a renewal row: whenever a re-verification is outstanding
// (the vendor replaced an expired approved document). The reviewer-side
// verdict lock is gone, so the buttons stay live even after the verdict has
// been notified — a change simply opens a new draft.
export function showReverifyButtons(submission: AdminSubmission | undefined): boolean {
  return submission?.reverify_requested_at != null;
}

export function documentRowData(submission: AdminSubmission | undefined): DocumentRowData {
  if (!submission) return { kind: 'awaiting' };

  const files = submission.submission_files ?? [];
  if (files.length > 0) {
    const primaryFile = pickPrimaryFile(files);
    return {
      kind: 'file',
      primaryFile,
      uploadedAt: latestCreatedAt(files),
      expiresAt: primaryFile.expires_at,
    };
  }

  const adoptions = submission.submission_adoptions ?? [];
  if (adoptions.length > 0) {
    const a = adoptions[0]!;
    return { kind: 'adopted', documentId: a.document_id, uploadedAt: a.created_at };
  }

  return { kind: 'awaiting' };
}
